import { type ComponentType, useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";

import { Mails } from "lucide-react";

import ModalConfirm from "../../components/ModalConfirm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import client from "../../client";
import { getCountryObjectFromCache } from "../../utils";
import { isExternalUser, type UserData } from "../../utils/roles";
import {
  listCountries,
  listCountryInfoHubDashboard,
  listCountryInfoHubSubjects,
  listInfoHubTopics,
  listInfoHubTopicsReset,
} from "../../actions";
import { ConnectedViewModal } from "./Components/ViewModal";
import SelectedCountryTable from "./Components/SelectedCountryTable";
import AllCountriesTable from "./Components/AllCountriesTable";
import type { EntityId, SubjectTopic } from "./types";
import type { CountryOption } from "./Components/SubjectsDataTable/columns";

// Legacy JS modal (out of scope for this migration); typed permissively for interop.
const ModalConfirmComponent = ModalConfirm as unknown as ComponentType<Record<string, unknown>>;

// Each tab in the array below can carry a different component with its own
// props, so normalise these to a shared loose type for the tabs mapping.
const SelectedCountryTableTab = SelectedCountryTable as unknown as ComponentType<
  Record<string, unknown>
>;
const AllCountriesTableTab = AllCountriesTable as unknown as ComponentType<Record<string, unknown>>;

const CONTENT_TYPES = [
  "Aggregator Information",
  "Farmer Training",
  "Input Distribution",
  "Equipment Distribution",
];

interface ViewModalOptions {
  isOpen: boolean;
  id?: EntityId | null;
}

interface ConfirmModalOptions {
  isOpen: boolean;
  id?: EntityId;
  type?: string;
}

interface StateProps {
  country: CountryOption;
  countries: CountryOption[];
  currentUser: UserData;
  topics: SubjectTopic[];
}

interface DispatchProps {
  listInfoHubTopics: () => void;
  listInfoHubTopicsReset: () => void;
  listCountries: () => void;
  refreshCountrySubjects: (countryId: EntityId) => void;
}

type InfoHubProps = StateProps & DispatchProps & RouteComponentProps;

export const InfoHub = ({
  history,
  country,
  countries,
  currentUser,
  topics,
  listInfoHubTopics,
  listInfoHubTopicsReset,
  listCountries,
  refreshCountrySubjects,
}: InfoHubProps) => {
  const { t } = useTranslation();
  // External users are locked to their own country, so the cross-country tab is hidden.
  const canViewAllCountries = !isExternalUser(currentUser);
  const [viewModalOptions, setViewModalOptions] = useState<ViewModalOptions>({
    isOpen: false,
    id: undefined,
  });
  const [confirmModalOptions, setConfirmModalOptions] = useState<ConfirmModalOptions>({
    isOpen: false,
    id: undefined,
    type: undefined,
  });

  useEffect(() => {
    listInfoHubTopics();
    listCountries();
    return () => {
      listInfoHubTopicsReset();
    };
  }, [listInfoHubTopics, listCountries, listInfoHubTopicsReset]);

  const types = CONTENT_TYPES.map(contentType => ({ label: contentType, value: contentType }));

  const updateSubjectStatus = (id: EntityId, status: string) => {
    client.patch(`/api/infohub_subjects/${id}/`, { status }).then(result => {
      if (result) {
        refreshCountrySubjects(country.id);
      }
    });
  };

  const resetConfirmModalOptions = () =>
    setConfirmModalOptions({ isOpen: false, id: undefined, type: undefined });

  const modalProps = {
    modalOptions: viewModalOptions,
    setModalOptions: setViewModalOptions,
    actions: {
      publish: (id: EntityId) => updateSubjectStatus(id, "published"),
      withdraw: (id: EntityId) => updateSubjectStatus(id, "withdrawn"),
    },
  };

  const generateConfirmModalValidation = (type?: string, id?: EntityId) => {
    switch (type) {
      case "copy":
        resetConfirmModalOptions();
        history.push(`/wfpManager/infohub/newContent/${id}`, { isCopy: true });
        return;
      default:
        resetConfirmModalOptions();
        return;
    }
  };

  const confirmModalProps = {
    isOpen: confirmModalOptions.isOpen,
    title: t("infoHub.confirmModal.copyTitle"),
    text: t("infoHub.confirmModal.copyText"),
    onValidate: () =>
      generateConfirmModalValidation(confirmModalOptions.type, confirmModalOptions.id),
    onCancel: () => resetConfirmModalOptions(),
  };

  // Pass the component type + props (not an inline wrapper) so the tab content
  // keeps a stable identity across renders and is not remounted on every render.
  const tabs = [
    {
      id: "tab1",
      Component: SelectedCountryTableTab,
      props: { country, topics, types, setViewModalOptions, setConfirmModalOptions },
      label: t("infoHub.contentsForCountry", { country: country.name }),
    },
    ...(canViewAllCountries
      ? [
          {
            id: "tab2",
            Component: AllCountriesTableTab,
            props: {
              country,
              topics,
              types,
              countries,
              setViewModalOptions,
              setConfirmModalOptions,
            },
            label: t("infoHub.allContents"),
          },
        ]
      : []),
  ];

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 p-2 text-blue-600">
              <Mails />
            </span>
            <h1 className="font-semibold text-2xl">{t("infoHub.longTitle")}</h1>
          </div>
        </div>
        <Tabs defaultValue={tabs[0].id}>
          <TabsList>
            {tabs.map(({ id, label }) => (
              <TabsTrigger key={id} value={id}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(({ id, Component, props }) => (
            <TabsContent key={id} value={id} className="mt-6">
              <Component {...props} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
      {viewModalOptions.id && <ConnectedViewModal {...modalProps} />}
      {confirmModalOptions.id && <ModalConfirmComponent {...confirmModalProps} />}
    </>
  );
};

export const mapStateToProps = (state: {
  listCountries: { data: { results?: CountryOption[] } };
  listInfoHubTopics: { data: { results?: SubjectTopic[] } };
  userDetail: { data: UserData };
}): StateProps => ({
  country: getCountryObjectFromCache(),
  countries: state.listCountries.data.results ?? [],
  currentUser: state.userDetail?.data ?? { user_groups: [] },
  topics: state.listInfoHubTopics.data.results ?? [],
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listInfoHubTopics: () => dispatchAction(dispatch, listInfoHubTopics()),
  listInfoHubTopicsReset: () => dispatchAction(dispatch, listInfoHubTopicsReset()),
  listCountries: () => dispatchAction(dispatch, listCountries()),
  refreshCountrySubjects: countryId => {
    dispatchAction(
      dispatch,
      listCountryInfoHubSubjects({
        country: countryId,
        page_size: 9999,
        page: 1,
        status: "published",
      })
    );
    dispatchAction(dispatch, listCountryInfoHubDashboard({ country: countryId }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(InfoHub));
